import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/donor`;

const DonorAuthContext = createContext();

export const useDonorAuth = () => {
  const context = useContext(DonorAuthContext);
  if (!context) {
    throw new Error('useDonorAuth must be used within a DonorAuthProvider');
  }
  return context;
};

export const DonorAuthProvider = ({ children }) => {
  const [donor, setDonor] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('donorToken'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('donorToken');
    const storedDonor = localStorage.getItem('donorInfo');
    
    if (storedToken && storedDonor) {
      try {
        setToken(storedToken);
        setDonor(JSON.parse(storedDonor));
      } catch {
        // Invalid stored data, clear it
        localStorage.removeItem('donorToken');
        localStorage.removeItem('donorInfo');
      }
    }
    setLoading(false);
  }, []);

  const register = useCallback(async (userData) => {
    try {
      const response = await axios.post(`${API}/register`, userData);
      const { access_token, donor_name, email } = response.data;
      
      localStorage.setItem('donorToken', access_token);
      localStorage.setItem('donorInfo', JSON.stringify({ name: donor_name, email }));
      
      setToken(access_token);
      setDonor({ name: donor_name, email });
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur lors de l\'inscription'
      };
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API}/login`, { email, password });
      const { access_token, donor_name, email: donorEmail } = response.data;
      
      localStorage.setItem('donorToken', access_token);
      localStorage.setItem('donorInfo', JSON.stringify({ name: donor_name, email: donorEmail }));
      
      setToken(access_token);
      setDonor({ name: donor_name, email: donorEmail });
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Courriel ou mot de passe incorrect'
      };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('donorToken');
    localStorage.removeItem('donorInfo');
    setToken(null);
    setDonor(null);
  }, []);

  const getAuthHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const value = useMemo(() => ({
    donor,
    token,
    loading,
    isAuthenticated: !!token,
    register,
    login,
    logout,
    getAuthHeaders,
  }), [donor, token, loading, register, login, logout, getAuthHeaders]);

  return (
    <DonorAuthContext.Provider value={value}>
      {children}
    </DonorAuthContext.Provider>
  );
};

export default DonorAuthContext;
